/**
 * Disclosures ("Good to know"): shared content + first-run acknowledgement.
 *
 * Six short notes (historical fiction, dark themes, saves, purchases,
 * feedback, anonymous analytics), laid out as two columns of three. Keep
 * the count even so the columns stay balanced (a test checks this).
 *
 * All shown by <DisclosuresModal />:
 *   - "firstRun" right after the welcome screen, before the tour starts,
 *     so a new player hears about dark themes and analytics before
 *     playing; and for a returning player who has not acknowledged the
 *     current version.
 *   - "review" from Settings → Good to Know.
 *
 * Version bump policy: if the substance changes (new pricing, a new kind
 * of heavy theme, a payment-processor change), bump the key suffix below
 * and every device sees the notes once more. Rewording alone needs no bump.
 */

import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { BarChart3, Coins, Landmark, MessageSquare, Save, ShieldAlert } from "lucide-react";
import { openSettings, type SettingsFocus } from "@/lib/settingsEvents";

/** Bumped when the disclosures meaningfully change. The version is
 *  encoded in the localStorage key so old keys naturally stop matching
 *  and every device re-prompts exactly once after a bump. */
// v2 (2026-09-30): added the anonymous analytics note, so every player
// sees the notes once more.
export const DISCLOSURES_ACK_KEY = "coe-disclosures-ack-v2";

/** Window event dispatched by Settings → Good to Know. Index listens for
 *  it and reopens the notes in review mode over the map. */
export const DISCLOSURES_REOPEN_EVENT = "coe-disclosures-reopen";

/** An inline link in a note that opens Settings on one control. */
function SettingsLink({ focus, children }: { focus: SettingsFocus; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={() => openSettings(focus)}
      className="text-primary underline underline-offset-2 hover:text-foreground"
    >
      {children}
    </button>
  );
}

const linkClass = "text-primary underline underline-offset-2 hover:text-foreground";

export interface DisclosureNote {
  Icon: LucideIcon;
  /** Short lead-in, one line. */
  heading: string;
  /** One or two plain sentences. */
  body: ReactNode;
}

export const DISCLOSURE_NOTES: DisclosureNote[] = [
  {
    Icon: Landmark,
    heading: "Real history, imagined lives",
    body: "The events and the famous names are real. Your character and their story are invented, and the historian's notes show where the two meet.",
  },
  {
    Icon: ShieldAlert,
    heading: "Some chapters are dark",
    body: "War, persecution, plague, slavery and famine appear where the history calls for them.",
  },
  {
    Icon: Save,
    heading: "Your progress is saved",
    body: (
      <>
        Automatically, on this device. <SettingsLink focus="account">Sign in</SettingsLink> from Settings to keep it
        on every device.
      </>
    ),
  },
  {
    Icon: Coins,
    heading: "Three chronicles are free",
    body: (
      <>
        The rest are <SettingsLink focus="purchases">one-time purchases</SettingsLink>: €1.49 each, or €14.99 for the
        Complete Atlas. Prices include tax, and{" "}
        <a href="https://paddle.net" target="_blank" rel="noopener noreferrer" className={linkClass}>
          Paddle
        </a>{" "}
        handles the payment.
      </>
    ),
  },
  {
    Icon: MessageSquare,
    heading: "We read every message",
    body: (
      <>
        Found something off, or want a chronicle we haven't made yet? Tell us through{" "}
        <SettingsLink focus="feedback">Send Feedback</SettingsLink> in Settings.
      </>
    ),
  },
  {
    Icon: BarChart3,
    heading: "Anonymous play statistics",
    body: (
      <>
        We count how the chronicles are played (starts, endings, errors) to improve them: never your name or
        email, and no cookies.{" "}
        <SettingsLink focus="analytics">Turn it off in Settings</SettingsLink> or see{" "}
        <a href="/privacy" className={linkClass}>
          what's collected
        </a>
        .
      </>
    ),
  },
];

// -------- Acknowledgement persistence (module-scope cached) ----------
//
// Per the project's localStorage-cache invariant: read once into a
// module-scope variable, invalidate on cross-tab `storage` events.

let cachedAck: boolean | null = null;

export function hasAckedDisclosures(): boolean {
  if (cachedAck !== null) return cachedAck;
  if (typeof window === "undefined") return false;
  try {
    cachedAck = window.localStorage.getItem(DISCLOSURES_ACK_KEY) === "1";
  } catch {
    cachedAck = false;
  }
  return cachedAck;
}

export function markDisclosuresAcked(): void {
  cachedAck = true;
  try {
    window.localStorage.setItem(DISCLOSURES_ACK_KEY, "1");
  } catch {
    /* ignore — private mode etc.; player still sees the modal again next
       launch, which is acceptable */
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === DISCLOSURES_ACK_KEY || e.key === null) cachedAck = null;
  });
}

/** Test-only: reset module-scope cache. `localStorage.clear()` does NOT
 *  fire a same-tab storage event, so tests that reset between cases
 *  need to invalidate the cache explicitly. */
export function _resetDisclosuresCacheForTests(): void {
  cachedAck = null;
}
